import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  const textColor = isScrolled ? "text-foreground-700" : "text-background-50";
  const textHover = isScrolled ? "hover:text-foreground-950" : "hover:text-background-100";
  const borderClass = isScrolled
    ? "border-secondary-300/60 hover:border-secondary-400"
    : "border-background-50/40 hover:border-background-50/70";
  const dropdownBg = isScrolled ? "bg-background-50 border-background-200/70" : "bg-background-50 border-background-200/70";
  const logoColor = isScrolled ? "text-foreground-950" : "text-background-50";
  const mobileBg = isScrolled
    ? "bg-background-50 border-t border-background-200/70"
    : "bg-background-50 border-t border-background-200/70";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-[72px]">
          <div className="flex items-center space-x-2">
            <Link to="/" className={`font-heading text-2xl md:text-3xl font-semibold tracking-tight whitespace-nowrap cursor-pointer ${logoColor}`}>
              itaobuy<span className="text-accent-500">.</span>casa
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className={`text-sm transition-colors font-label whitespace-nowrap cursor-pointer ${textColor} ${textHover}`}>
              Home
            </Link>
            <Link to="/blog/itaobuyspreadsheet-guide" className={`text-sm transition-colors font-label whitespace-nowrap cursor-pointer ${textColor} ${textHover}`}>
              Shopping Guide
            </Link>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center space-x-1.5 text-sm transition-colors font-label whitespace-nowrap cursor-pointer px-4 py-2 rounded-full border ${textColor} ${textHover} ${borderClass}`}
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>
              {isDropdownOpen && (
                <div className={`absolute top-full mt-2 right-0 w-72 border rounded-lg py-1 max-h-[480px] overflow-y-auto ${dropdownBg}`}>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center space-x-3 px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      <div className="w-10 h-10 flex-shrink-0 rounded-md overflow-hidden">
                        <img
                          src={cat.image}
                          alt={cat.name}
                          title={`Browse ${cat.name} on itaobuyspreadsheet`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="font-label text-foreground-800">{cat.name}</span>
                      <i className="ri-external-link-line text-xs text-foreground-400 ml-auto"></i>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              itaobuy sheet
            </a>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${isScrolled ? "text-foreground-800" : "text-background-50"}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`text-2xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className={`md:hidden py-4 ${mobileBg}`}>
            <div className="flex flex-col space-y-1">
              <Link
                to="/"
                className="px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-md transition-colors font-label cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-home-4-line mr-3"></i>
                Home
              </Link>
              <Link
                to="/blog/itaobuyspreadsheet-guide"
                className="px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-md transition-colors font-label cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <i className="ri-book-open-line mr-3"></i>
                Shopping Guide
              </Link>
              <div className="px-4 py-2">
                <p className="text-xs text-foreground-500 font-label uppercase tracking-wider mb-3">Categories</p>
                <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex flex-col items-center p-3 bg-background-100 hover:bg-background-200/70 rounded-lg transition-colors cursor-pointer"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="w-14 h-14 rounded-lg overflow-hidden mb-2">
                        <img
                          src={cat.image}
                          alt={cat.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <span className="text-xs font-label font-medium text-foreground-800 text-center leading-tight">
                        {cat.name}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
              <div className="px-4 pt-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="block w-full text-center px-5 py-3 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  itaobuy sheet
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}